import { z } from 'zod';

/**
 * Tax Details (W-9) form - only two fields for now.
 *
 * Alphanumeric + dashes for TIN; plain alphanumeric for VAT. If in the
 * future the buyer's Field Dictionary exposes more tax fields, extend
 * this schema here and in `convertTaxDetailsFormToVM`.
 */
export const TaxDetailsSchema = z
    .object({
        BusinessName: z
            .string()
            .trim()
            .optional()
            .transform((val) => val ?? ''),

        TIN: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^\d{9}$/, 'TIN must be 9 digits'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        VATNumber: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^[A-Za-z0-9]+$/, 'VAT Number may only contain letters and numbers'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
    })
    .superRefine(({ TIN, VATNumber, BusinessName }, ctx) => {
        if (!TIN && !VATNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['TIN'],
                message: 'At least one field must be filled out',
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['VATNumber'],
                message: 'At least one field must be filled out',
            });
        }
        if (TIN && !BusinessName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Business Name is required when TIN is provided',
                path: ['BusinessName'],
            });
        }
    });

export type TaxDetailsFormViewModel = z.infer<typeof TaxDetailsSchema>;

export function createEmptyTaxDetailsFormViewModel(businessName: string) {
    return {
        BusinessName: businessName,
        TIN: '',
        VATNumber: '',
    };
}
